import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';

/** Mirrors Flutter `GeneralDlListStatus` / account_screen.dart */
function generalDlStatusSummary(dl: GeneralDlRecord | null | undefined): string {
  if (!dl) return 'Not submitted';

  const submittedRaw = dl.submittedAt;
  const submittedStr = submittedRaw ? String(submittedRaw).trim() : '';
  let hasSubmission = submittedStr.length > 0 && submittedStr !== 'null';

  const adminOk = dl.isVerifiedByAdmin === true;
  const validOk = dl.isValid === true;
  const driverOk = dl.canBeAdditionalDriver === true;
  const reason = dl.rejectionReason ? String(dl.rejectionReason).trim() : '';
  const adminRejectedOutcome = adminOk && validOk !== true;

  if (!hasSubmission && !reason && !adminRejectedOutcome) {
    const idStr = dl.id ? String(dl.id).trim() : '';
    const expPresent = dl.expirationDate != null && String(dl.expirationDate).trim() !== '';
    if (idStr.length > 0 && expPresent && !adminOk) {
      hasSubmission = true;
    }
  }

  if (adminOk && validOk && driverOk) {
    const exp = dl.expirationDate ? new Date(dl.expirationDate) : null;
    const tail = exp && !Number.isNaN(exp.getTime()) ? `, expires ${exp.toLocaleDateString()}` : '';
    return `Verified${tail}.`;
  }

  if (reason || adminRejectedOutcome) {
    return reason ? `Not approved: ${reason}` : 'Not approved. Please redo your verification.';
  }

  if (hasSubmission) {
    return 'Submitted, waiting for admin review. Verification usually takes about 10 minutes.';
  }

  return 'Not submitted';
}

const MY_GENERAL_VERIFY_DL = gql`
  query MyGeneralVerifyDLModal {
    myGeneralVerifyDL {
      id
      isVerifiedByAdmin
      expirationDate
      birthDate
      legalFirstName
      legalMiddleName
      legalLastName
      isValid
      submittedAt
      verifiedAt
      rejectionReason
      canBeAdditionalDriver
    }
  }
`;

const GET_PRESIGNED_URL_FOR_GENERAL_DL = gql`
  mutation GetPresignedUrlForGeneralDL($fileType: String!, $side: DLImageSide!) {
    getPresignedUrlForGeneralDL(fileType: $fileType, side: $side) {
      url
      key
    }
  }
`;

const SUBMIT_GENERAL_DL_VERIFICATION = gql`
  mutation SubmitGeneralDLVerificationModal($input: SubmitGeneralDLVerificationInput!) {
    submitGeneralDLVerification(input: $input) {
      id
      isVerifiedByAdmin
      expirationDate
      birthDate
      legalFirstName
      legalMiddleName
      legalLastName
      isValid
      submittedAt
      rejectionReason
      canBeAdditionalDriver
    }
  }
`;

type GeneralDlRecord = {
  id?: string | null;
  isVerifiedByAdmin?: boolean | null;
  expirationDate?: string | null;
  birthDate?: string | null;
  legalFirstName?: string | null;
  legalMiddleName?: string | null;
  legalLastName?: string | null;
  isValid?: boolean | null;
  submittedAt?: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  canBeAdditionalDriver?: boolean | null;
};

const Overlay = styled.div`
  ${tw`
    fixed inset-0 z-[60] flex items-end sm:items-center justify-center
    bg-black bg-opacity-50 p-0 sm:p-4
  `}
`;

const Sheet = styled.div`
  ${tw`
    w-full max-w-lg bg-white rounded-t-2xl sm:rounded-modal shadow-overlay
    flex flex-col max-h-[92vh]
  `}
`;

const SheetScroll = styled.div`
  ${tw`overflow-y-auto flex-1 px-4 pt-4 pb-6`}
`;

const HeaderRow = styled.div`
  ${tw`flex items-start justify-between gap-2 mb-3`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary`}
`;

const CloseBtn = styled.button`
  ${tw`p-2 rounded-full text-text-secondary hover:bg-bg-secondary disabled:opacity-40`}
`;

const BodyText = styled.p`
  ${tw`text-sm text-text-primary mb-4`}
`;

const Hint = styled.p`
  ${tw`text-xs text-text-secondary mb-4`}
`;

const SlotLabel = styled.p`
  ${tw`text-xs text-text-secondary mb-1`}
`;

const Slot = styled.button<{ $hasImage?: boolean }>`
  ${tw`
    w-full rounded-card border-2 border-dashed border-border-default
    flex items-center justify-center overflow-hidden
    bg-bg-secondary aspect-[1.586/1] mb-4
    disabled:opacity-50 cursor-pointer
  `}
  ${({ $hasImage }) => ($hasImage ? tw`border-solid border-border-default p-0` : tw`p-4`)}
`;

const SlotImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const SlotPlaceholder = styled.span`
  ${tw`text-sm text-text-secondary text-center`}
`;

const SlotActions = styled.div`
  ${tw`flex flex-wrap gap-2 mb-4 -mt-2`}
`;

const LinkishBtn = styled.button`
  ${tw`text-xs font-medium text-black hover:text-black-hover underline`}
`;

const UploadedRow = styled.div`
  ${tw`flex items-center gap-2 text-sm text-green-700 mb-4`}
`;

const PrimaryBtn = styled.button`
  ${tw`
    w-full py-3 rounded-lg font-medium text-white bg-black
    hover:bg-black disabled:bg-gray-400 disabled:cursor-not-allowed
    flex items-center justify-center gap-2
  `}
`;

const SecondaryBtn = styled.button`
  ${tw`
    w-full py-3 rounded-lg font-medium border border-border-default text-text-primary bg-white
    hover:bg-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed
    flex items-center justify-center gap-2 mb-3
  `}
`;

const AttestOverlay = styled.div`
  ${tw`fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-40 p-4`}
`;

const AttestCard = styled.div`
  ${tw`bg-white rounded-card shadow-overlay max-w-md w-full p-6`}
`;

const AttestTitle = styled.h3`
  ${tw`text-lg font-semibold text-text-primary mb-4`}
`;

const CheckboxRow = styled.label`
  ${tw`flex gap-3 items-start mb-3 cursor-pointer text-sm text-text-primary`}
`;

const AttestActions = styled.div`
  ${tw`flex justify-end gap-2 mt-6`}
`;

const GhostBtn = styled.button`
  ${tw`px-4 py-2 rounded-lg border border-border-default text-text-primary hover:bg-bg-secondary`}
`;

const ConfirmBtn = styled.button`
  ${tw`px-4 py-2 rounded-lg bg-black text-white hover:bg-black disabled:bg-gray-400`}
`;

function isVerifiedApproved(dl: GeneralDlRecord | null | undefined): boolean {
  return !!(dl?.isVerifiedByAdmin && dl?.isValid && dl?.canBeAdditionalDriver);
}

export interface GeneralDriverLicenseVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after successful submit so parent can refetch list UI */
  onAfterSubmit?: () => void;
}

export function GeneralDriverLicenseVerificationModal({
  isOpen,
  onClose,
  onAfterSubmit,
}: GeneralDriverLicenseVerificationModalProps) {
  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);

  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [backFile, setBackFile] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);
  const [uploadedFrontKey, setUploadedFrontKey] = useState<string | null>(null);
  const [uploadedBackKey, setUploadedBackKey] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAttestation, setShowAttestation] = useState(false);
  const [legalOk, setLegalOk] = useState(false);
  const [ownsOk, setOwnsOk] = useState(false);

  const {
    data,
    loading: dlLoading,
    refetch,
  } = useQuery(MY_GENERAL_VERIFY_DL, {
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });

  const generalDL = data?.myGeneralVerifyDL as GeneralDlRecord | undefined;

  const [getPresigned] = useMutation(GET_PRESIGNED_URL_FOR_GENERAL_DL);
  const [submitVerification] = useMutation(SUBMIT_GENERAL_DL_VERIFICATION);

  const revokePreview = useCallback((url: string | null) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setFrontPreview(prev => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setBackPreview(prev => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
    setFrontFile(null);
    setBackFile(null);
    setUploadedFrontKey(null);
    setUploadedBackKey(null);
    setShowAttestation(false);
    setLegalOk(false);
    setOwnsOk(false);
  }, [isOpen]);

  const setFront = (file: File | null) => {
    revokePreview(frontPreview);
    setFrontFile(file);
    setUploadedFrontKey(null);
    setUploadedBackKey(null);
    setFrontPreview(file ? URL.createObjectURL(file) : null);
  };

  const setBack = (file: File | null) => {
    revokePreview(backPreview);
    setBackFile(file);
    setUploadedFrontKey(null);
    setUploadedBackKey(null);
    setBackPreview(file ? URL.createObjectURL(file) : null);
  };

  const statusText = useMemo(() => generalDlStatusSummary(generalDL ?? null), [generalDL]);

  const bothUploaded = !!uploadedFrontKey?.length && !!uploadedBackKey?.length;

  const canUploadPhotos = !!frontFile && !!backFile && !uploading && !submitting && !dlLoading;

  const canSubmit = bothUploaded && !uploading && !submitting && !dlLoading;

  const verifiedApproved = isVerifiedApproved(generalDL ?? null);

  const busy = uploading || submitting || dlLoading;

  const uploadFile = async (file: File, side: 'FRONT' | 'BACK') => {
    const { data: presign } = await getPresigned({
      variables: {
        fileType: file.type || 'image/jpeg',
        side,
      },
    });
    const upload = presign?.getPresignedUrlForGeneralDL;
    if (!upload?.url || !upload?.key) {
      throw new Error('Failed to create upload URL');
    }
    const res = await fetch(upload.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'image/jpeg' },
      body: file,
    });
    if (!res.ok) {
      throw new Error(`Failed to upload ${side.toLowerCase()} license image`);
    }
    return upload.key as string;
  };

  const handleUploadPhotos = async () => {
    if (!canUploadPhotos || !frontFile || !backFile) return;
    setUploading(true);
    try {
      const [fk, bk] = await Promise.all([
        uploadFile(frontFile, 'FRONT'),
        uploadFile(backFile, 'BACK'),
      ]);
      setUploadedFrontKey(fk);
      setUploadedBackKey(bk);
      toast.info(
        'Photos uploaded. Submit when ready — an admin verifies everything from your images.',
      );
    } catch (e: any) {
      setUploadedFrontKey(null);
      setUploadedBackKey(null);
      toast.error(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runSubmit = async () => {
    if (!uploadedFrontKey || !uploadedBackKey) return;
    setSubmitting(true);
    try {
      await submitVerification({
        variables: {
          input: {
            frontDriverLicenseUrl: uploadedFrontKey,
            backDriverLicenseUrl: uploadedBackKey,
            expirationDate: null,
            birthDate: null,
            legalFirstName: null,
            legalMiddleName: null,
            legalLastName: null,
          },
        },
      });
      toast.success('Verification submitted');
      await refetch();
      onAfterSubmit?.();
      onClose();
      toast.info('Verification usually takes about 10 minutes.');
    } catch (e: any) {
      toast.error(e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
      setShowAttestation(false);
    }
  };

  const handleSubmitClick = () => {
    if (!canSubmit) return;
    setLegalOk(false);
    setOwnsOk(false);
    setShowAttestation(true);
  };

  const viewFullSize = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay role="presentation" onClick={() => !busy && onClose()}>
        <Sheet role="dialog" aria-labelledby="dl-modal-title" onClick={e => e.stopPropagation()}>
          <SheetScroll>
            <HeaderRow>
              <Title id="dl-modal-title">Driver license verification</Title>
              <CloseBtn type="button" disabled={busy} onClick={onClose} aria-label="Close">
                <FontAwesomeIcon icon={faTimes} />
              </CloseBtn>
            </HeaderRow>

            <BodyText>{dlLoading ? 'Loading status…' : statusText}</BodyText>

            {generalDL &&
              [generalDL.legalFirstName, generalDL.legalMiddleName, generalDL.legalLastName].some(
                Boolean,
              ) && (
                <Hint>
                  Legal name on file:{' '}
                  {[generalDL.legalFirstName, generalDL.legalMiddleName, generalDL.legalLastName]
                    .filter(Boolean)
                    .join(' ')}
                </Hint>
              )}

            <Hint>
              Add clear photos of the front and back. Use the actions below to view or replace. When
              both sides are set, tap <strong>Upload photos</strong>, then{' '}
              <strong>Submit for admin review</strong>. On the mobile app we may read text from the
              license automatically; on web we send your photos for admin review only.
            </Hint>

            <input
              ref={frontInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => setFront(e.target.files?.[0] ?? null)}
            />
            <input
              ref={backInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => setBack(e.target.files?.[0] ?? null)}
            />

            <SlotLabel>Front</SlotLabel>
            <Slot
              type="button"
              disabled={busy}
              $hasImage={!!frontPreview}
              onClick={() => !busy && frontInputRef.current?.click()}
            >
              {frontPreview ? (
                <SlotImg src={frontPreview} alt="Driver license front" />
              ) : (
                <SlotPlaceholder>Tap to add front</SlotPlaceholder>
              )}
            </Slot>
            {frontPreview && (
              <SlotActions>
                <LinkishBtn type="button" onClick={() => viewFullSize(frontPreview)}>
                  View
                </LinkishBtn>
                <LinkishBtn type="button" onClick={() => !busy && frontInputRef.current?.click()}>
                  Replace
                </LinkishBtn>
                <LinkishBtn type="button" onClick={() => !busy && setFront(null)}>
                  Clear
                </LinkishBtn>
              </SlotActions>
            )}

            <SlotLabel>Back</SlotLabel>
            <Slot
              type="button"
              disabled={busy}
              $hasImage={!!backPreview}
              onClick={() => !busy && backInputRef.current?.click()}
            >
              {backPreview ? (
                <SlotImg src={backPreview} alt="Driver license back" />
              ) : (
                <SlotPlaceholder>Tap to add back</SlotPlaceholder>
              )}
            </Slot>
            {backPreview && (
              <SlotActions>
                <LinkishBtn type="button" onClick={() => viewFullSize(backPreview)}>
                  View
                </LinkishBtn>
                <LinkishBtn type="button" onClick={() => !busy && backInputRef.current?.click()}>
                  Replace
                </LinkishBtn>
                <LinkishBtn type="button" onClick={() => !busy && setBack(null)}>
                  Clear
                </LinkishBtn>
              </SlotActions>
            )}

            {bothUploaded && (
              <UploadedRow>
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Photos uploaded</span>
              </UploadedRow>
            )}

            <SecondaryBtn type="button" disabled={!canUploadPhotos} onClick={handleUploadPhotos}>
              {uploading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Uploading…
                </>
              ) : (
                'Upload photos'
              )}
            </SecondaryBtn>

            <PrimaryBtn type="button" disabled={!canSubmit} onClick={handleSubmitClick}>
              {submitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Submitting…
                </>
              ) : verifiedApproved ? (
                'Update and resubmit'
              ) : (
                'Submit verification'
              )}
            </PrimaryBtn>
          </SheetScroll>
        </Sheet>
      </Overlay>

      {showAttestation && (
        <AttestOverlay onClick={() => !submitting && setShowAttestation(false)}>
          <AttestCard onClick={e => e.stopPropagation()}>
            <AttestTitle>Confirm verification</AttestTitle>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={legalOk}
                onChange={e => setLegalOk(e.target.checked)}
              />
              <span>
                I confirm this driver license is legally valid for driving this vehicle — it has not
                been suspended and is not reported lost at the time I am submitting this
                verification.
              </span>
            </CheckboxRow>
            <CheckboxRow>
              <input type="checkbox" checked={ownsOk} onChange={e => setOwnsOk(e.target.checked)} />
              <span>
                I confirm this driver license is mine and does not belong to another person.
              </span>
            </CheckboxRow>
            <AttestActions>
              <GhostBtn
                type="button"
                disabled={submitting}
                onClick={() => setShowAttestation(false)}
              >
                Cancel
              </GhostBtn>
              <ConfirmBtn
                type="button"
                disabled={!legalOk || !ownsOk || submitting}
                onClick={() => runSubmit()}
              >
                Confirm
              </ConfirmBtn>
            </AttestActions>
          </AttestCard>
        </AttestOverlay>
      )}
    </>
  );
}

export default GeneralDriverLicenseVerificationModal;
